import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import { fetchSingleCbdOrder } from "@/redux/slice/cbd-slice/cbd-slice";
import useAuth from "@/hooks/useAuth";
import { Clock, IceCream2, User } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { repeatOrderApi } from "@/api/take-order/take-order";
import toast from "react-hot-toast";
import { setOrderData } from "@/redux/slice/take-order-slice/order-slice";
import { useNavigate } from "react-router-dom";
import { setSelectedCustomer } from "@/redux/slice/take-order-slice/selected-customer-slice";
import { setDeliveryInfo } from "@/redux/slice/take-order-slice/delivery-slice";
import hasPermission from "@/hooks/hasPermission";
import getOrderAdvanceDetails from "@/lib/orderAdvanceUtils";
import getOrderTipsDetails from "@/lib/orderTipsUtils";
import getOrderVatDetails from "@/lib/orderVatUtils";
import { 
  OrderItemPriceDisplay, 
  OrderItemComboBreakdown, 
  OrderItemCourseBreakdown, 
  OrderItemMixAndMatchBreakdown, 
  OrderItemCustomizationBreakdown, 
  OrderItemSpecialMenuBreakdown 
} from "@/components/TakeOder/OrderItemComboBreakdown";
import OrderedItemDetailsModal from "@/components/TakeOder/OrderedItemDetailsModal";

function SingleOrderDialog({ isOpen, onClose, customer }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { singleOrder } = useSelector((state) => state.cbd.cbd);
  const [showRepeatConfirm, setShowRepeatConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewItemDetails, setViewItemDetails] = useState(null);
  const [isTabletView, setIsTabletView] = useState(typeof window !== "undefined" ? window.innerWidth <= 1200 : false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsTabletView(window.innerWidth <= 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && customer?.selectedOrder && user?.restaurant_id) {
      setIsLoading(true);
      dispatch(fetchSingleCbdOrder(customer?.selectedOrder)).then(() =>
        setIsLoading(false),
      );
    }
  }, [isOpen, customer?.selectedOrder, user?.restaurant_id, dispatch]);

  if (!isOpen) return null;

  const order = singleOrder || {};
  const orderItems = order?.items || [];
  const customer_info = order?.customer || {};

  const getStatusColor = (stage) => {
    switch (stage?.toLowerCase()) {
      case "on_going":
        return "text-cgreen";
      case "completed":
        return "text-cblue";
      case "cancelled":
        return "text-red-500";
      default:
        return "text-cyellow";
    }
  };

  const getExtras = (item) => {
    const extras = [];

    if (item.modifiers && item.modifiers.length > 0) {
      item.modifiers.forEach((m) => {
        extras.push(`${m.preFix ? m.preFix + " " : ""}${m.name}`);
      });
    }

    const otherKeys = [
      "addons",
      "salads",
      "sauces",
      "toppings",
      "extra_addons",
      "extra_toppings",
      "extra_sauces",
      "extra_salads",
      "new_items",
      "banquet_selected_items",
      "banquet_extra_items",
    ];

    otherKeys.forEach((key) => {
      if (item[key] && item[key].length > 0) {
        item[key].forEach((ext) => {
          if (ext.name) extras.push(ext.name);
        });
      }
    });

    return extras;
  };


  function newOrder() {
    // Implement new order functionality here
    console.log(customer_info);
    dispatch(setSelectedCustomer(customer_info));
    dispatch(setDeliveryInfo(customer_info?.active_address))
    navigate("/take-order");
  }

  function handleRepeatOrderClick() {
    setShowRepeatConfirm(true);
  }

  function confirmRepeatOrder() {
    setShowRepeatConfirm(false);
    console.log("Repeat order clicked for order ID:", order.id);
    repeatOrderApi(user.restaurant_id, order.id)
      .then((res) => {
        console.log("Order repeated successfully:", res);

        toast.success("Order placed successfully");
        dispatch(setOrderData(res?.data));
        navigate("/take-order", { state: { from: "cbd", orderDetailsId: res?.data?.id } });
      })
      .catch((err) => {
        console.error("Error repeating order:", err);
        toast.error("Failed to repeat order");
      });
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 bg-[#1A1A1A] border-none text-cwhite overflow-hidden flex flex-col">
        <DialogTitle></DialogTitle>
        <div className="flex flex-col md:flex-row h-full overflow-hidden">
          {/* Left side - Order Details */}
          <div className="flex-1 p-4 md:p-6 flex flex-col overflow-hidden">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <h2 className="text-lg md:text-xl font-bold mb-1">
                  Order ID: {order?.id || "n/a"}
                </h2>
                <div className="space-y-0.5">
                  <p className="text-cwhite3 text-sm md:text-base">
                    Customer Name:{" "}
                    {order?.customer_full_name ||
                      customer_info?.full_name ||
                      "n/a"}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-cwhite2 opacity-80">
                    <span>
                      Phone:{" "}
                      {order?.customer_number || customer_info?.number || "n/a"}
                    </span>
                    <span>Email: {customer_info?.email || "n/a"}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-row sm:flex-col items-center sm:items-end gap-4 sm:gap-1 text-right w-full sm:w-auto justify-between sm:justify-end">
                <div className="flex items-center gap-1.5 text-cgreen font-medium text-sm md:text-base">
                  <Clock size={16} />
                  <span>{order?.cooking_time || 0}min</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-cyellow text-sm md:text-base capitalize font-medium">
                    {order?.order_type ? String(order.order_type).replace(/_/g, " ") : "n/a"}
                  </span>
                  <div className="flex items-center gap-1 text-cwhite3 text-sm md:text-base">
                    <IceCream2 size={16} />
                    <span>{orderItems.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items List */}
            <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar space-y-2">
              {isLoading ? (
                <div className="h-full flex items-center justify-center text-cwhite3 text-sm">
                  Loading order details...
                </div>
              ) : (
                <>
                  {orderItems?.length > 0 ? (
                    orderItems?.map((item, i) => (
                      <div
                        key={item?.order_item_id || item?.id || i}
                        className="bg-[#262626] rounded-md p-3 flex flex-col gap-2 group hover:bg-[#2c2c2c] transition-colors border border-transparent hover:border-[#383838]"
                      >
                        {/* Item Header Row */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm md:text-base font-semibold text-white">
                                {item?.name || "N/A"}
                              </span>
                              {item?.variant?.name && (
                                <span className="text-xs text-color6 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                                  ({item.variant.name})
                                </span>
                              )}
                              {item?.size && (
                                <span className="text-xs text-color6 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                                  ({item.size})
                                </span>
                              )}
                              {item?.buffet_price_for && (
                                <span className="text-xs text-color6 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                                  ({item.buffet_price_for === "adult" ? "Adult" : "Kids"})
                                </span>
                              )}
                              <span className="text-xs font-bold text-color6 bg-[#1f1f1f] px-2 py-0.5 rounded border border-[#333]">
                                x{item?.quantity || 1}
                              </span>
                              {item?.is_offer_discount && (
                                <span className="text-[10px] bg-emerald-600/20 text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 rounded font-medium">
                                  🏷️ Discount Offer
                                </span>
                              )}
                              {item?.is_complementary && (
                                <span className="text-[10px] bg-purple-500/20 text-purple-300 border border-purple-500/40 px-1.5 py-0.5 rounded font-medium">
                                  ✨ Complementary
                                </span>
                              )}
                              {item?.is_replaced && (
                                <span className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/40 px-1.5 py-0.5 rounded font-medium">
                                  Replaced
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Price & View Details Action */}
                          <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-4">
                            <OrderItemPriceDisplay item={item} orderType={order?.order_type} />
                            <button
                              type="button"
                              onClick={() => setViewItemDetails(item)}
                              className="text-cyellow hover:text-yellow-300 text-xs font-medium underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              View Details
                            </button>
                          </div>
                        </div>

                        {/* Structured Breakdowns */}
                        <OrderItemComboBreakdown comboItems={item?.combo_items} isTabletView={isTabletView} />
                        <OrderItemCourseBreakdown courseItems={item?.course_items} item={item} isTabletView={isTabletView} />
                        <OrderItemSpecialMenuBreakdown specialMenu={item?.special_menu} item={item} isTabletView={isTabletView} />
                        <OrderItemMixAndMatchBreakdown items={item?.mix_and_match_items || item?.selected_items || item?.categories} isTabletView={isTabletView} />
                        <OrderItemCustomizationBreakdown item={item} isTabletView={isTabletView} />

                        {/* Replaced item new items list if present */}
                        {item?.new_items && item?.new_items.length > 0 && (
                          <div className="mt-1 pt-1.5 border-t border-[#383838] flex flex-col gap-1.5">
                            <span className="text-[11px] font-semibold text-color7">New Replacement Items:</span>
                            {item.new_items.map((newItem, nIdx) => (
                              <div key={nIdx} className="bg-[#1f1f1f] rounded p-2 flex flex-col gap-1 border border-[#333]">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-medium text-white">{newItem.name}</span>
                                    {newItem?.variant?.name && (
                                      <span className="text-[10px] text-color6">({newItem.variant.name})</span>
                                    )}
                                    <span className="text-[10px] text-color6">x{newItem.quantity || 1}</span>
                                    <span className="px-1.5 py-0.2 bg-color7/20 text-color7 border border-color7/40 text-[9px] rounded">New</span>
                                  </div>
                                  <OrderItemPriceDisplay item={newItem} orderType={order?.order_type} />
                                </div>
                                <OrderItemComboBreakdown comboItems={newItem?.combo_items} isTabletView={isTabletView} />
                                <OrderItemCourseBreakdown courseItems={newItem?.course_items} item={newItem} isTabletView={isTabletView} />
                                <OrderItemSpecialMenuBreakdown specialMenu={newItem?.special_menu} item={newItem} isTabletView={isTabletView} />
                                <OrderItemMixAndMatchBreakdown items={newItem?.mix_and_match_items || newItem?.selected_items || newItem?.categories} isTabletView={isTabletView} />
                                <OrderItemCustomizationBreakdown item={newItem} isTabletView={isTabletView} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="h-full bg-[#262626] min-h-[200px] rounded-md flex items-center justify-center text-cwhite3 italic text-sm">
                      No data Available
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Note Section */}
            <div className="mt-4 bg-[#262626] rounded-md p-3 text-sm">
              <p className="text-cwhite3">
                <span className="font-semibold text-cwhite">Note:</span>{" "}
                {order?.note || "n/a"}
              </p>
            </div>
          </div>

          {/* Right side - Billing Details */}
          <div className="w-full md:w-[320px] lg:w-[360px] bg-[#2E2E2E] p-4 md:p-6 flex flex-col border-t md:border-t-0 md:border-l border-[#333] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Billing Details</h3>
              <span className="px-3 py-0.5 text-xs bg-cblue text-cwhite font-semibold rounded-full">Preview</span>
            </div>

            {(() => {
              const discountAmount = Number(order?.discount_amount || 0);
              const loyaltyDiscountAmount = Number(order?.loyalty_discount_amount || 0);
              const totalSavings = order?.total_savings !== undefined && order?.total_savings !== null
                ? Number(order?.total_savings)
                : (discountAmount + loyaltyDiscountAmount + Number(order?.applied_spend_offer?.discount_amount || 0));
              const appliedDiscounts = order?.discounts || [];
              const appliedLoyaltyDiscounts = order?.loyalty_discounts || [];
              const isPaid = order?.payment_status === "paid";
              const paidAmount = order?.payment?.pay_amount ?? (isPaid ? (order?.total_price || 0) : 0);
              const changeAmt = order?.payment?.change_amount || (paidAmount > (order?.total_price || 0) ? (paidAmount - (order?.total_price || 0)) : 0);
              const unpaidAmt = isPaid ? 0 : Math.max(0, (order?.total_price || 0) - (order?.payment?.pay_amount || 0));

              return (
                <div className="flex-1 space-y-2.5 text-[#E0E0E0] text-xs md:text-sm">
                  <div className="flex justify-between items-center">
                    <span>Order Total</span>
                    <span className="font-medium">{formatCurrency(order?.order_total || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Discount</span>
                    <span className="font-medium">{formatCurrency(discountAmount)}</span>
                  </div>
                  {appliedDiscounts.map((discount, index) => (
                    <div
                      key={`discount-${discount?.id || index}`}
                      className="flex justify-between items-center text-xs pl-3 text-color7"
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] bg-emerald-600/35 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-normal">
                          - {discount?.discount_name || discount?.name || "Discount"}
                        </span>
                        {discount?.discount_value && (
                          <span className="text-[10px] bg-emerald-600/35 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-normal">
                            {discount?.discount_type === "percentage" || discount?.type === "percentage" ? `${discount?.discount_value}%` : `£${discount?.discount_value}`}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold text-color7">
                        £ -{Math.abs(Number(discount?.discount_amount || 0)).toFixed(2)}
                      </span>
                    </div>
                  ))}

                  <div className="flex justify-between items-center">
                    <span>Loyalty Discount</span>
                    <span className="font-medium">{formatCurrency(loyaltyDiscountAmount)}</span>
                  </div>
                  {appliedLoyaltyDiscounts.map((loyalty, index) => (
                    <div
                      key={`loyalty-${loyalty?.loyalty_short_code || index}`}
                      className="flex justify-between items-center text-xs pl-3 text-color7"
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] bg-emerald-600/35 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-normal">
                          - {loyalty?.loyalty_short_code || loyalty?.loyalty_discount_code || "Code"}
                        </span>
                        {loyalty?.loyalty_discount_amount && (
                          <span className="text-[10px] bg-emerald-600/35 text-emerald-400 border border-emerald-500/50 px-1 py-0.2 rounded font-normal">
                            {formatCurrency(Number(loyalty?.loyalty_discount_amount))}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold text-color7">
                        {loyalty?.free_items && loyalty?.free_items.length > 0 ? (
                          `${loyalty.free_items.length} x Free Item`
                        ) : (
                          `£ -${Math.abs(Number(loyalty?.loyalty_discount_amount || 0)).toFixed(2)}`
                        )}
                      </span>
                    </div>
                  ))}

                  <div className="flex justify-between items-center">
                    <span>Points ({order?.used_points || 0})</span>
                    <span className="font-medium">{formatCurrency(order?.points_value || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Service Charge</span>
                    <span className="font-medium">{formatCurrency(order?.service_charge_amount || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center text-color7">
                    <span>Total Savings</span>
                    <span className="font-medium">{formatCurrency(totalSavings)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Sub Total</span>
                    <span className="font-medium">{formatCurrency(order?.subtotal || 0)}</span>
                  </div>

                  {(() => {
                    const vatInfo = getOrderVatDetails(order);
                    return (
                      <div className="flex justify-between items-center">
                        <span>Total Vat {vatInfo.vatTypeLabel}</span>
                        <span className="font-medium">{formatCurrency(order?.total_vat || 0)}</span>
                      </div>
                    );
                  })()}

                  {(() => {
                    const tipsInfo = getOrderTipsDetails(order);
                    return (
                      <>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span>Tips</span>
                            {tipsInfo.tipsMode === "course_wise" && (
                              <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Course-wise</span>
                            )}
                            {tipsInfo.tipsMode === "split" && (
                              <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Split</span>
                            )}
                          </div>
                          <span className={`font-medium ${tipsInfo.totalTips > 0 ? "text-yellow-400 font-bold" : ""}`}>
                            {formatCurrency(tipsInfo.totalTips)}
                          </span>
                        </div>
                        {tipsInfo.breakdown && tipsInfo.breakdown.length > 0 && (
                          <div className="-mt-1 mb-1 pl-2 pr-1.5 py-1 bg-black/30 rounded border border-white/5 flex flex-col gap-0.5">
                            {tipsInfo.breakdown.map((person, idx) => (
                              <div key={idx} className="flex justify-between items-center text-[11px] text-neutral-400">
                                <span className="truncate">
                                  • {person.payerName} {person.tipType === "percentage" ? `(${person.tipInputAmount}%)` : `(Fixed)`}:
                                </span>
                                <span className="font-medium text-neutral-300 shrink-0">
                                  {formatCurrency(person.tipAmount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Advance Amount */}
                  {(() => {
                    const advanceInfo = getOrderAdvanceDetails(order);
                    if (!advanceInfo.hasAdvance || !advanceInfo.isPaid) return null;
                    return (
                      <div className="flex justify-between items-center text-emerald-400 bg-emerald-950/20 px-2 py-1 rounded border border-emerald-800/30">
                        <span className="flex items-center gap-1 font-medium">
                          💳 {advanceInfo.label} {advanceInfo.uniqueId && `(${advanceInfo.uniqueId})`}
                        </span>
                        <span className="font-semibold">- {formatCurrency(advanceInfo.advanceAmount)}</span>
                      </div>
                    );
                  })()}

                  <div className="flex justify-between items-center text-cwhite border-b border-dashed border-[#555] pb-2">
                    <span className="font-semibold text-yellow-500">Bill Amount</span>
                    <span className="font-semibold">{formatCurrency(order?.total_price ?? order?.order_total ?? 0)}</span>
                  </div>

                  <div className="flex justify-between items-center text-base md:text-lg font-bold text-cwhite pt-2">
                    <span>TOTAL TO PAY</span>
                    <span>{formatCurrency(order?.total_price || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span>Paid</span>
                    <span className="font-medium">{formatCurrency(paidAmount)}</span>
                  </div>

                  {changeAmt > 0 && (
                    <div className="flex justify-between items-center">
                      <span>Changed Amount</span>
                      <span className="font-medium">{formatCurrency(changeAmt)}</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-color14">
                    <span className="font-semibold">UNPAID AMOUNT</span>
                    <span className="font-semibold">{formatCurrency(unpaidAmt)}</span>
                  </div>

                  <div className="pt-4 mt-auto flex justify-between items-end">
                    <span
                      className={`font-bold text-lg ${getStatusColor(order?.stage)}`}
                    >
                      {order?.stage === "on_going"
                        ? "UNPAID"
                        : order?.stage?.toUpperCase() || "n/a"}
                    </span>
                    <span className="text-2xl font-bold text-cwhite opacity-50">
                      {order?.day_order_number
                        ? order.day_order_number.toString().padStart(3, "0")
                        : "000"}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div className="mt-6 space-y-2.5">
              {
                hasPermission("cbd.cbd.add_new_order") && (<Button onClick={newOrder} className="w-full h-10 md:h-11 bg-cgreen hover:bg-cgreen/90 text-cwhite font-bold text-sm md:text-base rounded-md">
                  Add New Order
                </Button>)
              }
              <div className="grid grid-cols-2 gap-2.5">
                <Button onClick={handleRepeatOrderClick} className="h-10 md:h-11 bg-cblue hover:bg-cblue/90 text-cwhite font-bold text-sm rounded-md">
                  Repeat Order
                </Button>
                <Button
                  onClick={onClose}
                  className="h-10 md:h-11 bg-cred hover:bg-cred/90 text-cwhite font-bold text-sm rounded-md"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>

        {showRepeatConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="w-full max-w-sm rounded-lg border border-cblack6 bg-cblack4 p-6 shadow-2xl animate-fadeIn">
              <h3 className="text-lg font-bold text-cyellow">Repeat Order</h3>
              <p className="mt-3 text-sm text-cwhite3 leading-relaxed">
                Are you sure you want to repeat this order? This will place a new order with the same items.
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowRepeatConfirm(false)}
                  className="px-4 py-2 text-xs font-semibold text-cwhite3 bg-cblack3 hover:bg-cblack5 border border-cblack6 rounded transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmRepeatOrder}
                  className="px-4 py-2 text-xs font-semibold text-cwhite bg-cblue hover:bg-cblue/90 rounded transition-all active:scale-95"
                >
                  Yes, Repeat
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {viewItemDetails && (
      <OrderedItemDetailsModal
        isOpen={Boolean(viewItemDetails)}
        onClose={() => setViewItemDetails(null)}
        item={viewItemDetails}
        orderType={order?.order_type}
        isTabletView={isTabletView}
      />
    )}
  </>
  );
}

export default SingleOrderDialog;
